import { getApp } from 'firebase/app'
import { getBlob, getDownloadURL, getStorage, ref } from 'firebase/storage'
import { chunk } from 'lodash'
import { store } from 'providers/store'
import { putIsLoading } from 'middleware/actions/response'
import { setInvoiceDownloadFailureDialogState, setPaymentReceiptDownloadFailureDialogState } from 'slices/download/DownloadSlice'
import { hasExceptionImageSuffix, REGEX } from 'utils/validations'
import { get } from './lodash'
import Constants from '../Constants'
import { cloudStorageErrorHandler } from './error'

const IMAGES_ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png']
const IMPORT_SEQUENCE_EXTENSTION = ['xlsx']

const DOWNLOAD_TYPE = {
  pdf: 'application/pdf',
  excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  png: 'data:image/png',
  json: 'application/json',
}

const { ASSETS_BUCKET_NAME, IMAGE_UPLOADS_PRIVATE_BUCKET_NAME, VISION_VIDEOS_BUCKET_NAME } = Constants

// Return file extension
export const getFileExtension = fileName => {
  if (typeof fileName !== 'string') {
    return fileName
  }

  const lastIndex = fileName.toLowerCase().lastIndexOf('.')

  return lastIndex > -1 ? fileName.substr(lastIndex + 1) : ''
}

// Validate if it is an image by its filename
export const isImage = fileName => IMAGES_ALLOWED_EXTENSIONS.includes(getFileExtension(fileName))

export const isImageType = contentType => contentType && contentType.startsWith('image/')

export const isPdfType = contentType => contentType === 'application/pdf'

export const getFileNameWithoutExtension = fileName => {
  if (!fileName) return ''
  const lastDot = fileName.lastIndexOf('.')
  return lastDot > -1 ? fileName.substring(0, lastDot) : fileName
}
export const isXlsxFormat = fileName => IMPORT_SEQUENCE_EXTENSTION.includes(getFileExtension(fileName))

export const isValidFileSize = (size, maxSize) => size / (1024 * 1024) > parseInt(maxSize.replace(/D/g, ''), 10)

export const downloadFile = (data, fileName, type = 'pdf') => {
  const url = window.URL.createObjectURL(new Blob([data], { type: get(DOWNLOAD_TYPE, type) }))
  const link = document.createElement('a')
  link.style.display = 'none'
  link.href = url
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
}

const convertBase64toBlob = (content, contentType = '') => {
  const sliceSize = 512
  const byteCharacters = window.atob(content)

  const byteArrays = []
  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize)
    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }

    byteArrays.push(new Uint8Array(byteNumbers))
  }

  return new Blob(byteArrays, { type: contentType })
}

export const printPreviewBase64PDF = (data, type = 'application/pdf') => {
  let blob = null
  blob = convertBase64toBlob(data, type)
  const blobURL = URL.createObjectURL(blob)
  const windowRef = window.open(blobURL)
  const docRef = windowRef.document
  const scriptRef = document.createElement('script')

  function injectThis() {
    window.print()
  }

  scriptRef.innerHTML = `window.onload = ${injectThis.toString()};`
  docRef.body.appendChild(scriptRef)
}

export const blobToBase64 = blob => {
  return new Promise(resolve => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.readAsDataURL(blob)
  })
}

export const requestBlobFromUrl = url => {
  return new Promise(resolve => {
    const xhr = new XMLHttpRequest()
    xhr.responseType = 'blob'

    xhr.addEventListener('load', () => {
      const blob = xhr.response
      resolve(blob)
    })
    xhr.open('GET', url)
    xhr.send()
  })
}

export const getFileNameFromHeaders = (headers, name, date) => {
  try {
    let fileName = headers['content-disposition'].split(';')[1].split('filename=')[1]
    if (/^".+"$/.test(fileName)) {
      fileName = fileName.substr(1).slice(0, -1)
    }
    return fileName
  } catch (e) {
    return [name, date].join('_').replace(/ /g, '_')
  }
}

export const getFileBlob = file => {
  const firebaseApp = getApp()
  const storage = getStorage(firebaseApp, `gs://${ASSETS_BUCKET_NAME}`)
  const fileRef = ref(storage, file)
  return getBlob(fileRef)
}

export const getPaymentReceiptBlob = (businessId, accountId, accountTransactionHistoryId) =>
  getFileBlob(`${businessId}/accounts/${accountId}/payments/${accountTransactionHistoryId.toString()}.pdf`)

export const getInvoiceBlob = (invoiceNumber, businessId) => getFileBlob(`${businessId}/INVOICE/${invoiceNumber.toString()}.pdf`)

export const getStatementBlob = (statementNumber, businessId) => getFileBlob(`${businessId}/STATEMENT/${statementNumber}.pdf`)

export const downloadInvoiceBlob = async (invoiceNumber, businessId) =>
  getInvoiceBlob(invoiceNumber, businessId)
    .then(blob => {
      downloadFile(blob, invoiceNumber)
      return Promise.resolve({ invoiceNumber, success: true })
    })
    .catch(() => Promise.resolve({ invoiceNumber, success: false }))

export const downloadInvoiceBlobListInChunks = async (invoiceNumberList, businessId) => {
  let invoiceResult = []
  const invoiceNumberDonwloadListOfChunks = chunk(invoiceNumberList, 2)
  for (const invoiceNumberChunkList of invoiceNumberDonwloadListOfChunks) {
    const dowloadBlobInvoiceList = invoiceNumberChunkList.map(invoiceNumber => downloadInvoiceBlob(invoiceNumber, businessId))
    const response = await Promise.all(dowloadBlobInvoiceList)
    if (Array.isArray(response)) {
      invoiceResult = [...invoiceResult, ...response]
    }
    await new Promise(r => setTimeout(r, 500))
  }

  const failedInvoices = invoiceResult.filter(({ success }) => !success)
  if (failedInvoices.length > 0) {
    store.dispatch(
      setInvoiceDownloadFailureDialogState({ isOpen: true, invoices: failedInvoices.map(({ invoiceNumber }) => invoiceNumber) })
    )
  }
}

const MEDIUM_PHOTO_SUFFIX = '_1024x1024'
const SMALL_PHOTO_SUFFIX = '_260x260'

const PHOTO_SIZE_TO_SUFFIX = {
  default: '',
  medium: MEDIUM_PHOTO_SUFFIX,
  small: SMALL_PHOTO_SUFFIX,
}

const fetchBlobFromBucket = async (storage, path) => {
  try {
    const imageRef = ref(storage, path)
    return getBlob(imageRef)
  } catch (error) {
    console.log(error)
  }
  return null
}

export const getExceptionImageBlob = async fileLocation => {
  const firebaseApp = getApp()
  const bucketStorage = getStorage(firebaseApp, `gs://${IMAGE_UPLOADS_PRIVATE_BUCKET_NAME}`)
  const bucketBlob = await fetchBlobFromBucket(bucketStorage, fileLocation)
  if (bucketBlob) {
    return bucketBlob
  }
  throw new Error('Image not found in bucket')
}

export const getPrivateAssetBlob = async path => {
  const firebaseApp = getApp()
  const bucketStorage = getStorage(firebaseApp, `gs://${ASSETS_BUCKET_NAME}`)
  const bucketBlob = await fetchBlobFromBucket(bucketStorage, path)
  if (bucketBlob) {
    return bucketBlob
  }
  throw new Error('File not found in bucket')
}

export const getVisionVideoDownloadUrl = async videoPath => {
  if (!videoPath) return ''
  if (!VISION_VIDEOS_BUCKET_NAME) {
    console.warn('VISION_VIDEOS_BUCKET_NAME is not set')
    return ''
  }
  try {
    const firebaseApp = getApp()
    const storage = getStorage(firebaseApp, `gs://${VISION_VIDEOS_BUCKET_NAME}`)
    const videoRef = ref(storage, videoPath)
    return await getDownloadURL(videoRef)
  } catch (error) {
    console.error('Failed to get vision video download URL', error)
    return ''
  }
}

// Vision detections (the /api/v1/detections endpoint) reference media as full
// gs:// URIs that carry their own bucket, e.g.
// gs://dev1-vision-core-edge-upload/<path>. Parse out the bucket + object path
// and mint a Firebase download URL the browser can render. Returns '' on
// failure so callers fall back to an empty/placeholder state.
export const resolveVisionGsUri = async gsUri => {
  if (!gsUri || !gsUri.startsWith('gs://')) return gsUri || ''
  try {
    const withoutScheme = gsUri.slice('gs://'.length)
    const slashIndex = withoutScheme.indexOf('/')
    if (slashIndex < 0) return ''
    const bucket = withoutScheme.slice(0, slashIndex)
    const objectPath = withoutScheme.slice(slashIndex + 1)
    // gs:///path (no bucket) or gs://bucket/ (no object) would mint an invalid
    // Storage handle/ref and throw — treat both as failures.
    if (!bucket || !objectPath) return ''
    const storage = getStorage(getApp(), `gs://${bucket}`)
    return await getDownloadURL(ref(storage, objectPath))
  } catch (error) {
    console.error('Failed to resolve vision gs:// uri', gsUri, error)
    return ''
  }
}

export const getExceptionImageBlobFromPath = async (path, size) => {
  const firebaseApp = getApp()
  const bucketStorage = getStorage(firebaseApp, `gs://${IMAGE_UPLOADS_PRIVATE_BUCKET_NAME}`)

  const hasSuffixPattern = path ? hasExceptionImageSuffix(path) : false

  let suffix = ''
  if (!hasSuffixPattern && size in PHOTO_SIZE_TO_SUFFIX) {
    suffix = PHOTO_SIZE_TO_SUFFIX[size]
  }

  try {
    const imageRef = ref(bucketStorage, `${path}${suffix}`)
    return await getBlob(imageRef)
  } catch (error) {
    // attempt to fetch original img if suffix image DNE
    if (suffix) {
      try {
        const pathWithoutSuffix = path.replace(REGEX.HAS_EXCEPTION_IMAGE_SIZE_SUFFIX, '')
        const originalImageRef = ref(bucketStorage, pathWithoutSuffix)
        return await getBlob(originalImageRef)
      } catch (fallbackError) {
        console.error(`Failed to load original image at ${path}`, fallbackError)
        throw fallbackError
      }
    }

    throw error
  }
}

export const getGcsJsonData = async gcsPath => {
  try {
    const blob = await getFileBlob(gcsPath)
    const text = await blob.text()
    return JSON.parse(text)
  } catch (error) {
    const wrappedError = new Error(`Failed to retrieve or parse JSON from GCS path: ${gcsPath}`)
    wrappedError.cause = error
    throw wrappedError
  }
}

export const downloadInvoiceGroupBatch = async (businessId, groupId, groupName) => {
  const firebaseApp = getApp()
  const storage = getStorage(firebaseApp, `gs://${ASSETS_BUCKET_NAME}`)
  const invoiceBatchRef = ref(storage, `${businessId}/invoice_batch/${groupId}.json`)

  getBlob(invoiceBatchRef)
    .then(blob => downloadFile(blob, groupName, 'json'))
    .catch(cloudStorageErrorHandler)
}

export const downloadPaymentReceiptBlob = async (businessId, accountId, paymentNumber, accountTransactionHistoryId) =>
  getPaymentReceiptBlob(businessId, accountId, accountTransactionHistoryId)
    .then(blob => {
      downloadFile(blob, paymentNumber)
      return Promise.resolve({ paymentNumber, accountTransactionHistoryId, success: true })
    })
    .catch(() => Promise.resolve({ paymentNumber, accountTransactionHistoryId, success: false }))

export const downloadPaymentReceiptBlobListInChunks = async (businessId, paymentReceiptList, showLoader = false) => {
  if (showLoader) store.dispatch(putIsLoading(true))

  let paymentReceiptResult = []
  const listOfChunks = chunk(paymentReceiptList, 2)
  for (const paymentReceiptChunk of listOfChunks) {
    const dowloadBlobReceiptList = paymentReceiptChunk.map(({ paymentNumber, accountTransactionHistoryId, accountId }) =>
      downloadPaymentReceiptBlob(businessId, accountId, paymentNumber, accountTransactionHistoryId)
    )
    const response = await Promise.all(dowloadBlobReceiptList)
    if (Array.isArray(response)) {
      paymentReceiptResult = [...paymentReceiptResult, ...response]
    }
    await new Promise(r => setTimeout(r, 500))
  }

  const failedPaymentReceipts = paymentReceiptResult.filter(({ success }) => !success)
  if (failedPaymentReceipts.length > 0) {
    store.dispatch(
      setPaymentReceiptDownloadFailureDialogState({
        isOpen: true,
        receipts: failedPaymentReceipts.map(({ paymentNumber }) => paymentNumber),
      })
    )
  }

  if (showLoader) store.dispatch(putIsLoading(false))
}
